import { useState, type InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

/**
 * حقل كلمة سر بزرّ إظهار/إخفاء (#55).
 *
 * الافتراضي مخفي. الزرّ خارج ترتيب التنقّل بالمفاتيح (`tabIndex=-1`)
 * حتى لا يقف المستخدم عليه وهو ينتقل من الحقل إلى زرّ الدخول، ويبقى
 * قابلاً للنقر وبتسمية للقارئ الصوتي.
 *
 * واحد لكل شاشات كلمة السر — الدخول، والتغيير، والاستعادة — لا نسخة في
 * كل صفحة تنحرف عن أختها.
 */
/*
  بعض المتصفّحات تزرع عيناً خاصّة بها داخل حقول `type="password"` (سامسونج
  إنترنت على أندرويد، وEdge — والأخير يُخفى بـ CSS). فتصير عينان. على هذه
  المتصفّحات يُعرض الحقل نصّاً مموَّهاً بالنقاط (`-webkit-text-security`)
  بدل حقل كلمة سر حقيقي — نفس المظهر، عين واحدة.
*/
const NATIVE_EYE = typeof navigator !== 'undefined' && /SamsungBrowser/i.test(navigator.userAgent);

export function PasswordInput(props: Props) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(false);
  const masked = !shown && NATIVE_EYE;

  return (
    <span className="password-field">
      <input
        {...props}
        type={shown || masked ? 'text' : 'password'}
        style={masked ? { ...(props.style ?? {}), WebkitTextSecurity: 'disc' } as React.CSSProperties : props.style}
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
      />
      <button
        type="button"
        className="password-toggle"
        tabIndex={-1}
        aria-pressed={shown}
        aria-label={shown ? t('password.hide') : t('password.show')}
        title={shown ? t('password.hide') : t('password.show')}
        onClick={() => setShown((value) => !value)}
      >
        {shown ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
      </button>
    </span>
  );
}
